<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import type { MaskData, SamMask } from '@/models/RobVision';
import {
  requiredRule,
  noSpacesRule,
  useObjectSizeRule,
} from '@/helpers/FormRules';
import {
  findConnectedComponents,
  keepLargestComponent,
  fillHoles,
  rasterizeOBB,
  rasterizePolygon,
  type OrientedBBox,
} from '@/helpers/MaskAnalysis';
import {
  OBB_HANDLE_SIZE,
  OBB_KNOB_OFFSET,
  OBB_KNOB_RADIUS,
  POINT_HIT_RADIUS,
} from '@/helpers/RobvisionConstants';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

interface PromptPoint {
  point: [number, number]; // normalized 0..1 in image space
  label: 0 | 1;
}

type InputMode = 'segmentation' | 'drawing' | 'bbox' | 'polygon' | 'pan';
type ViewMode = 'image' | 'overlay' | 'mask-only';
type PromptMode = 'positive' | 'negative';
type OBBHandle = 'tl' | 'tr' | 'br' | 'bl' | 'rotate' | 'body';
type DragOp =
  | { kind: 'pan'; startX: number; startY: number }
  | { kind: 'draw' }
  | { kind: 'create-obb'; anchorX: number; anchorY: number }
  | { kind: 'translate-obb'; lastX: number; lastY: number }
  | { kind: 'resize-obb'; corner: 0 | 1 | 2 | 3 }
  | { kind: 'rotate-obb' };

const props = withDefaults(
  defineProps<{
    imageSrc: string | null;
    embeddingsReady: boolean;
    currentMask?: SamMask | null;
    existingClassNames: string[];
    minDimension?: number;
    maxDimension?: number;
    // Persisted state from the parent that should rehydrate the tool when
    // the user navigates back to a previously-labeled image. The parent's
    // labeledMasks/imageClasses/imageSizes are the source of truth across
    // step navigation; without these props the component remounted blank
    // and the user could destroy their work with a single brush stroke.
    initialMask?: MaskData | null;
    initialClassName?: string;
    initialSize?: { width: number; height: number; depth: number } | null;
    // True when another (lower-index) image owns this class's dimensions:
    // the size fields mirror the owner's values and are not editable here.
    sizeLocked?: boolean;
  }>(),
  {
    minDimension: 10,
    maxDimension: 2000,
    currentMask: null,
    initialMask: null,
    initialClassName: '',
    initialSize: null,
    sizeLocked: false,
  },
);

const objectSizeRule = useObjectSizeRule(
  props.minDimension,
  props.maxDimension,
);

const emit = defineEmits<{
  'create-mask': [points: Array<{ point: [number, number]; label: number }>];
  'set-class': [{ className: string }];
  'set-size': [{ width: number; height: number; depth: number }];
  'validity-changed': [isValid: boolean];
  'has-pixels-changed': [hasPixels: boolean];
  maskUpdated: [maskData: MaskData];
}>();

const loadingSpinnerStore = useLoadingSpinnerStore();
const maskCanvas = ref<HTMLCanvasElement | null>(null);
const imageElement = ref<HTMLImageElement | null>(null);

// The `let` bindings below are non-reactive per-instance state. In Vue 3
// `<script setup>` the script body IS the setup function, so each component
// instance gets its own closure — these are not module-scoped.
let imageLoaded = false;
let editableMaskData: Uint8ClampedArray | null = null;
let lastDrawPos: { x: number; y: number } | null = null;

const points = ref<PromptPoint[]>([]);
const obb = ref<OrientedBBox | null>(null);
const polygonPoints = ref<Array<[number, number]>>([]);
const hoveredPolygonVertex = ref<number>(-1);
const polygonPreviewPos = ref<[number, number] | null>(null);

const inputMode = ref<InputMode>('segmentation');
const viewMode = ref<ViewMode>('overlay');
const promptMode = ref<PromptMode>('positive');
const isErasing = ref(false);
const brushSize = ref(5);
const zoomLevel = ref(1.0);
const panOffset = ref({ x: 0, y: 0 });

const ccCount = ref(1);
const ignoreDisconnected = ref(false);
const hasMaskPixels = ref(false);

interface ModeDialogState {
  title: string;
  message: string;
  onConfirm: () => void;
}
const modeDialog = ref<ModeDialogState | null>(null);

let activeDrag: DragOp | null = null;
let cursorOverride: string | null = null;
const hoveredPointIndex = ref<number>(-1);

const className = ref('');
const sizeWidth = ref<number | null>(null);
const sizeHeight = ref<number | null>(null);
const sizeDepth = ref<number | null>(null);

const inputIsValid = ref(false);
const form = ref();

const showSingleInstanceHint = ref(true);
function dismissSingleInstanceHint() {
  showSingleInstanceHint.value = false;
}

const isMaskInvalid = computed(
  () => ccCount.value > 1 && !ignoreDisconnected.value,
);

const isStructurallyValid = computed(() => !isMaskInvalid.value);

const hasAnyContent = computed(
  () =>
    hasMaskPixels.value ||
    points.value.length > 0 ||
    obb.value !== null ||
    polygonPoints.value.length > 0,
);

watch(isStructurallyValid, () => emitValidity());

function emitValidity() {
  emit('validity-changed', inputIsValid.value && isStructurallyValid.value);
}

const getMousePos = (e: MouseEvent) => {
  if (!maskCanvas.value) {
    return null;
  }
  const rect = maskCanvas.value.getBoundingClientRect();
  const x = (e.clientX - rect.left - panOffset.value.x) / zoomLevel.value;
  const y = (e.clientY - rect.top - panOffset.value.y) / zoomLevel.value;
  return { x, y };
};

function pointHitTest(imgX: number, imgY: number): number {
  if (!imageElement.value) {
    return -1;
  }
  const w = imageElement.value.width;
  const h = imageElement.value.height;
  const radiusImg = POINT_HIT_RADIUS / zoomLevel.value;
  for (let i = points.value.length - 1; i >= 0; i--) {
    const p = points.value[i];
    const px = p.point[0] * w;
    const py = p.point[1] * h;
    if (Math.hypot(px - imgX, py - imgY) <= radiusImg) {
      return i;
    }
  }
  return -1;
}

function obbCornersWorld(b: OrientedBBox): Array<[number, number]> {
  const halfW = b.width / 2;
  const halfH = b.height / 2;
  const cosA = Math.cos(b.angleRad);
  const sinA = Math.sin(b.angleRad);
  const local: Array<[number, number]> = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];
  return local.map(([lx, ly]) => [
    b.cx + lx * cosA - ly * sinA,
    b.cy + lx * sinA + ly * cosA,
  ]);
}

function obbKnobWorld(b: OrientedBBox): [number, number] {
  const d = b.height / 2 + OBB_KNOB_OFFSET / zoomLevel.value;
  return [b.cx + d * Math.sin(b.angleRad), b.cy - d * Math.cos(b.angleRad)];
}

function obbHitTest(imgX: number, imgY: number): OBBHandle | null {
  const b = obb.value;
  if (!b) {
    return null;
  }
  const handleR = OBB_HANDLE_SIZE / zoomLevel.value;
  const knobR = OBB_KNOB_RADIUS / zoomLevel.value + 2;

  const [kx, ky] = obbKnobWorld(b);
  if (Math.hypot(kx - imgX, ky - imgY) <= knobR) {
    return 'rotate';
  }
  const corners = obbCornersWorld(b);
  const labels: OBBHandle[] = ['tl', 'tr', 'br', 'bl'];
  for (let i = 0; i < 4; i++) {
    const [cx, cy] = corners[i];
    if (Math.abs(cx - imgX) <= handleR && Math.abs(cy - imgY) <= handleR) {
      return labels[i];
    }
  }
  // Body hit-test: transform into local frame.
  const cosA = Math.cos(-b.angleRad);
  const sinA = Math.sin(-b.angleRad);
  const dx = imgX - b.cx;
  const dy = imgY - b.cy;
  const lx = dx * cosA - dy * sinA;
  const ly = dx * sinA + dy * cosA;
  if (Math.abs(lx) <= b.width / 2 && Math.abs(ly) <= b.height / 2) {
    return 'body';
  }
  return null;
}

function rasterizeCurrentOBB(updateCC: boolean = false) {
  if (!obb.value || !editableMaskData || !imageElement.value) {
    return;
  }
  editableMaskData.fill(0);
  rasterizeOBB(
    obb.value,
    editableMaskData,
    imageElement.value.width,
    imageElement.value.height,
  );
  if (updateCC) {
    updateConnectivity();
  } else {
    // OBB is always a single rectangle, so a live drag never produces a
    // disconnected mask. Skip the CCL pass until mouseup.
    ccCount.value = 1;
  }
}

function commitOBBToMask() {
  if (!obb.value) {
    return;
  }
  rasterizeCurrentOBB(true);
  obb.value = null;
}

function polygonVertexHitTest(imgX: number, imgY: number): number {
  const radiusImg = POINT_HIT_RADIUS / zoomLevel.value;
  for (let i = 0; i < polygonPoints.value.length; i++) {
    const [px, py] = polygonPoints.value[i];
    if (Math.hypot(px - imgX, py - imgY) <= radiusImg) {
      return i;
    }
  }
  return -1;
}

function rasterizeCurrentPolygon(updateCC: boolean = true) {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  editableMaskData.fill(0);
  if (polygonPoints.value.length >= 3) {
    rasterizePolygon(
      polygonPoints.value,
      editableMaskData,
      imageElement.value.width,
      imageElement.value.height,
    );
  }
  if (updateCC) {
    updateConnectivity();
  } else {
    ccCount.value = 1;
  }
}

function handlePolygonClick(imgX: number, imgY: number) {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  const w = imageElement.value.width;
  const h = imageElement.value.height;
  if (imgX < 0 || imgX > w || imgY < 0 || imgY > h) {
    return;
  }
  const hit = polygonVertexHitTest(imgX, imgY);
  if (hit >= 0) {
    polygonPoints.value.splice(hit, 1);
  } else {
    polygonPoints.value.push([imgX, imgY]);
  }
  rasterizeCurrentPolygon(true);
  redraw();
  emitMaskUpdate();
}

function updateConnectivity() {
  if (!editableMaskData || !imageElement.value) {
    ccCount.value = 0;
    hasMaskPixels.value = false;
    return;
  }
  const cc = findConnectedComponents(
    editableMaskData,
    imageElement.value.width,
    imageElement.value.height,
  );
  ccCount.value = cc.numComponents;
  hasMaskPixels.value = cc.numComponents > 0;
  if (cc.numComponents <= 1) {
    ignoreDisconnected.value = false;
  }
}

function emitMaskUpdate() {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  emit('maskUpdated', {
    data: new Uint8Array(editableMaskData),
    width: imageElement.value.width,
    height: imageElement.value.height,
  });
  emit('has-pixels-changed', hasMaskPixels.value);
}

function effectiveLabelForEvent(forceNegative: boolean): 0 | 1 {
  if (forceNegative) {
    return 0;
  }
  return promptMode.value === 'negative' ? 0 : 1;
}

function handleSegmentationClick(e: MouseEvent, forceNegative: boolean) {
  if (!maskCanvas.value || !imageElement.value) {
    return;
  }
  if (!props.embeddingsReady) {
    console.warn(
      '[LabelingTool] click ignored: embeddings not ready yet for this image.',
    );
    return;
  }
  const pos = getMousePos(e);
  if (!pos) {
    return;
  }
  const w = imageElement.value.width;
  const h = imageElement.value.height;

  const hitIdx = pointHitTest(pos.x, pos.y);
  if (hitIdx >= 0) {
    points.value.splice(hitIdx, 1);
    if (points.value.length === 0) {
      clearMaskBuffer();
      redraw();
      emitMaskUpdate();
    } else {
      requestSamMask();
    }
    return;
  }

  const normalizedX = pos.x / w;
  const normalizedY = pos.y / h;
  if (
    normalizedX < 0 ||
    normalizedX > 1 ||
    normalizedY < 0 ||
    normalizedY > 1
  ) {
    return;
  }
  points.value.push({
    point: [normalizedX, normalizedY],
    label: effectiveLabelForEvent(forceNegative),
  });
  requestSamMask();
}

function requestSamMask() {
  if (points.value.length === 0) {
    return;
  }
  // Strip Vue reactivity: structured-clone (postMessage) can't carry Proxies.
  const plain = points.value.map((p) => ({
    point: [p.point[0], p.point[1]] as [number, number],
    label: p.label,
  }));
  emit('create-mask', plain);
  loadingSpinnerStore.setShowLoadingOverlay(
    true,
    false,
    $gettext('Generating mask'),
    'surface',
  );
}

function clearMaskBuffer() {
  if (!editableMaskData) {
    return;
  }
  editableMaskData.fill(0);
  ccCount.value = 0;
  hasMaskPixels.value = false;
  ignoreDisconnected.value = false;
}

function handleClearPoints() {
  points.value = [];
  clearMaskBuffer();
  redraw();
  emitMaskUpdate();
}

function handleUndoLastPoint() {
  if (points.value.length === 0) {
    return;
  }
  points.value.pop();
  if (points.value.length === 0) {
    clearMaskBuffer();
    redraw();
    emitMaskUpdate();
  } else {
    requestSamMask();
  }
}

function handleKeepLargest() {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  keepLargestComponent(
    editableMaskData,
    imageElement.value.width,
    imageElement.value.height,
  );
  updateConnectivity();
  redraw();
  emitMaskUpdate();
}

function handleFillHoles() {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  fillHoles(
    editableMaskData,
    imageElement.value.width,
    imageElement.value.height,
  );
  updateConnectivity();
  redraw();
  emitMaskUpdate();
}

function handleIgnoreDisconnected() {
  ignoreDisconnected.value = true;
}

const handleContextMenu = (e: MouseEvent) => {
  e.preventDefault();
  if (inputMode.value === 'segmentation') {
    handleSegmentationClick(e, true);
  }
};

const handleMouseDown = (e: MouseEvent) => {
  if (!maskCanvas.value || !imageElement.value) {
    return;
  }
  // Pan: middle-click or Ctrl+left-click in any mode.
  if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
    e.preventDefault();
    activeDrag = {
      kind: 'pan',
      startX: e.clientX - panOffset.value.x,
      startY: e.clientY - panOffset.value.y,
    };
    return;
  }
  if (e.button !== 0) {
    return;
  }

  // Pan mode: left-click drags the image.
  if (inputMode.value === 'pan') {
    e.preventDefault();
    activeDrag = {
      kind: 'pan',
      startX: e.clientX - panOffset.value.x,
      startY: e.clientY - panOffset.value.y,
    };
    applyCursor();
    return;
  }

  if (inputMode.value === 'drawing') {
    activeDrag = { kind: 'draw' };
    const pos = getMousePos(e);
    if (pos) {
      drawAtPosition(pos.x, pos.y);
      lastDrawPos = pos;
      redraw();
    }
    return;
  }

  if (inputMode.value === 'bbox') {
    const pos = getMousePos(e);
    if (!pos) {
      return;
    }
    const handle = obbHitTest(pos.x, pos.y);
    if (handle === 'rotate') {
      activeDrag = { kind: 'rotate-obb' };
      return;
    }
    if (handle && handle !== 'body') {
      const cornerIdx: 0 | 1 | 2 | 3 =
        handle === 'tl' ? 0 : handle === 'tr' ? 1 : handle === 'br' ? 2 : 3;
      activeDrag = { kind: 'resize-obb', corner: cornerIdx };
      return;
    }
    if (handle === 'body') {
      activeDrag = { kind: 'translate-obb', lastX: pos.x, lastY: pos.y };
      return;
    }
    activeDrag = { kind: 'create-obb', anchorX: pos.x, anchorY: pos.y };
    obb.value = {
      cx: pos.x,
      cy: pos.y,
      width: 1,
      height: 1,
      angleRad: 0,
    };
    return;
  }

  // segmentation: click handled on mouseup so we can still detect drag-pan
};

const handleMouseMove = (e: MouseEvent) => {
  if (!maskCanvas.value) {
    return;
  }

  if (activeDrag?.kind === 'pan') {
    panOffset.value.x = e.clientX - activeDrag.startX;
    panOffset.value.y = e.clientY - activeDrag.startY;
    redraw();
    return;
  }

  const pos = getMousePos(e);

  if (activeDrag?.kind === 'draw') {
    if (!pos || !editableMaskData) {
      return;
    }
    if (lastDrawPos) {
      const dx = pos.x - lastDrawPos.x;
      const dy = pos.y - lastDrawPos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      const steps = Math.max(1, Math.ceil(distance));
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        drawAtPosition(lastDrawPos.x + dx * t, lastDrawPos.y + dy * t);
      }
    } else {
      drawAtPosition(pos.x, pos.y);
    }
    lastDrawPos = pos;
    redraw();
    return;
  }

  if (activeDrag?.kind === 'create-obb' && pos) {
    const a = activeDrag;
    const x0 = Math.min(a.anchorX, pos.x);
    const x1 = Math.max(a.anchorX, pos.x);
    const y0 = Math.min(a.anchorY, pos.y);
    const y1 = Math.max(a.anchorY, pos.y);
    obb.value = {
      cx: (x0 + x1) / 2,
      cy: (y0 + y1) / 2,
      width: Math.max(2, x1 - x0),
      height: Math.max(2, y1 - y0),
      angleRad: 0,
    };
    rasterizeCurrentOBB();
    redraw();
    return;
  }

  if (activeDrag?.kind === 'translate-obb' && pos && obb.value) {
    obb.value.cx += pos.x - activeDrag.lastX;
    obb.value.cy += pos.y - activeDrag.lastY;
    activeDrag.lastX = pos.x;
    activeDrag.lastY = pos.y;
    rasterizeCurrentOBB();
    redraw();
    return;
  }

  if (activeDrag?.kind === 'resize-obb' && pos && obb.value) {
    const corners = obbCornersWorld(obb.value);
    const oppIdx = (activeDrag.corner + 2) % 4;
    const [ox, oy] = corners[oppIdx];
    const newCx = (pos.x + ox) / 2;
    const newCy = (pos.y + oy) / 2;
    const dx = pos.x - ox;
    const dy = pos.y - oy;
    const cosA = Math.cos(-obb.value.angleRad);
    const sinA = Math.sin(-obb.value.angleRad);
    const lx = dx * cosA - dy * sinA;
    const ly = dx * sinA + dy * cosA;
    obb.value = {
      cx: newCx,
      cy: newCy,
      width: Math.max(2, Math.abs(lx)),
      height: Math.max(2, Math.abs(ly)),
      angleRad: obb.value.angleRad,
    };
    rasterizeCurrentOBB();
    redraw();
    return;
  }

  if (activeDrag?.kind === 'rotate-obb' && pos && obb.value) {
    obb.value.angleRad =
      Math.atan2(pos.y - obb.value.cy, pos.x - obb.value.cx) + Math.PI / 2;
    rasterizeCurrentOBB();
    redraw();
    return;
  }

  // Hover cursor hints in bbox mode.
  if (inputMode.value === 'bbox' && obb.value && pos) {
    const handle = obbHitTest(pos.x, pos.y);
    cursorOverride =
      handle === 'rotate'
        ? 'crosshair'
        : handle === 'tl' || handle === 'br'
          ? 'nwse-resize'
          : handle === 'tr' || handle === 'bl'
            ? 'nesw-resize'
            : handle === 'body'
              ? 'grab'
              : null;
    applyCursor();
  }

  // Hover-to-remove feedback in segmentation mode.
  if (inputMode.value === 'segmentation' && pos) {
    const idx = pointHitTest(pos.x, pos.y);
    if (idx !== hoveredPointIndex.value) {
      hoveredPointIndex.value = idx;
      cursorOverride = idx >= 0 ? 'pointer' : null;
      applyCursor();
      redraw();
    }
  } else if (hoveredPointIndex.value !== -1) {
    hoveredPointIndex.value = -1;
    redraw();
  }

  // Hover + live-preview feedback in polygon mode.
  if (inputMode.value === 'polygon' && pos) {
    const idx = polygonVertexHitTest(pos.x, pos.y);
    let needsRedraw = false;
    if (idx !== hoveredPolygonVertex.value) {
      hoveredPolygonVertex.value = idx;
      cursorOverride = idx >= 0 ? 'pointer' : null;
      applyCursor();
      needsRedraw = true;
    }
    // Preview the next vertex when not hovering an existing one and
    // we have at least one placed vertex to draw a ghost line from.
    const newPreview =
      idx === -1 && polygonPoints.value.length >= 1
        ? ([pos.x, pos.y] as [number, number])
        : null;
    const prev = polygonPreviewPos.value;
    const previewChanged =
      (newPreview === null) !== (prev === null) ||
      (newPreview !== null &&
        prev !== null &&
        (newPreview[0] !== prev[0] || newPreview[1] !== prev[1]));
    if (previewChanged) {
      polygonPreviewPos.value = newPreview;
      needsRedraw = true;
    }
    if (needsRedraw) {
      redraw();
    }
  } else {
    let needsRedraw = false;
    if (hoveredPolygonVertex.value !== -1) {
      hoveredPolygonVertex.value = -1;
      needsRedraw = true;
    }
    if (polygonPreviewPos.value !== null) {
      polygonPreviewPos.value = null;
      needsRedraw = true;
    }
    if (needsRedraw) {
      redraw();
    }
  }
};

const handleMouseUp = (e: MouseEvent) => {
  const wasDrag = activeDrag;
  activeDrag = null;
  lastDrawPos = null;

  if (!maskCanvas.value || !imageElement.value) {
    return;
  }

  if (wasDrag?.kind === 'pan') {
    applyCursor();
    return;
  }

  if (
    wasDrag?.kind === 'draw' ||
    wasDrag?.kind === 'translate-obb' ||
    wasDrag?.kind === 'resize-obb' ||
    wasDrag?.kind === 'rotate-obb' ||
    wasDrag?.kind === 'create-obb'
  ) {
    updateConnectivity();
    redraw();
    emitMaskUpdate();
    return;
  }

  // Pure click in segmentation mode.
  if (inputMode.value === 'segmentation' && e.button === 0) {
    handleSegmentationClick(e, false);
  }

  // Pure click in polygon mode.
  if (inputMode.value === 'polygon' && e.button === 0) {
    const pos = getMousePos(e);
    if (pos) {
      handlePolygonClick(pos.x, pos.y);
    }
  }
};

const handleMouseLeave = () => {
  activeDrag = null;
  lastDrawPos = null;
  cursorOverride = null;
  let needsRedraw = false;
  if (hoveredPointIndex.value !== -1) {
    hoveredPointIndex.value = -1;
    needsRedraw = true;
  }
  if (hoveredPolygonVertex.value !== -1) {
    hoveredPolygonVertex.value = -1;
    needsRedraw = true;
  }
  if (polygonPreviewPos.value !== null) {
    polygonPreviewPos.value = null;
    needsRedraw = true;
  }
  if (needsRedraw) {
    redraw();
  }
  applyCursor();
};

const drawAtPosition = (x: number, y: number) => {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  const px = Math.round(x);
  const py = Math.round(y);
  const { width, height } = imageElement.value;
  const radius = Math.round(brushSize.value / zoomLevel.value);
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const dist = dx * dx + dy * dy;
      if (dist <= radius * radius) {
        const nx = px + dx;
        const ny = py + dy;
        if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
          editableMaskData[ny * width + nx] = isErasing.value ? 0 : 255;
        }
      }
    }
  }
};

function drawMaskOverlay(ctx: CanvasRenderingContext2D) {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  const w = imageElement.value.width;
  const h = imageElement.value.height;
  const overlay = document.createElement('canvas');
  overlay.width = w;
  overlay.height = h;
  const overlayCtx = overlay.getContext('2d');
  if (!overlayCtx) {
    return;
  }
  const imageData = overlayCtx.createImageData(w, h);
  const out = imageData.data;
  const opaque = viewMode.value === 'mask-only';
  const numPixels = w * h;
  for (let i = 0; i < numPixels; i++) {
    if (editableMaskData[i] > 0) {
      const o = i * 4;
      if (opaque) {
        out[o] = 255;
        out[o + 1] = 255;
        out[o + 2] = 255;
        out[o + 3] = 255;
      } else {
        out[o] = 0;
        out[o + 1] = 114;
        out[o + 2] = 189;
        out[o + 3] = 128;
      }
    }
  }
  overlayCtx.putImageData(imageData, 0, 0);
  ctx.drawImage(overlay, 0, 0);
}

function drawPointMarkers(ctx: CanvasRenderingContext2D) {
  if (!imageElement.value || points.value.length === 0) {
    return;
  }
  const w = imageElement.value.width;
  const h = imageElement.value.height;
  const baseR = 7 / zoomLevel.value;
  const lw = 2 / zoomLevel.value;
  for (let i = 0; i < points.value.length; i++) {
    const p = points.value[i];
    const isHovered = i === hoveredPointIndex.value;
    const r = isHovered ? baseR * 1.5 : baseR;
    const cx = p.point[0] * w;
    const cy = p.point[1] * h;

    if (isHovered) {
      // Faint halo to telegraph "remove on click"
      ctx.beginPath();
      ctx.arc(cx, cy, r + 4 / zoomLevel.value, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.fill();
    }

    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = isHovered
      ? '#6b7280'
      : p.label === 1
        ? '#22c55e'
        : '#ef4444';
    ctx.fill();
    ctx.lineWidth = lw;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();

    if (isHovered) {
      // Draw an X to make "click to remove" obvious.
      ctx.beginPath();
      const off = r * 0.5;
      ctx.moveTo(cx - off, cy - off);
      ctx.lineTo(cx + off, cy + off);
      ctx.moveTo(cx + off, cy - off);
      ctx.lineTo(cx - off, cy + off);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / zoomLevel.value;
      ctx.stroke();
      continue;
    }

    if (p.label === 0) {
      ctx.beginPath();
      ctx.moveTo(cx - r * 0.5, cy);
      ctx.lineTo(cx + r * 0.5, cy);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / zoomLevel.value;
      ctx.stroke();
    }
  }
}

function drawOBBOverlay(ctx: CanvasRenderingContext2D) {
  const b = obb.value;
  if (!b) {
    return;
  }
  const corners = obbCornersWorld(b);
  ctx.beginPath();
  ctx.moveTo(corners[0][0], corners[0][1]);
  for (let i = 1; i < 4; i++) {
    ctx.lineTo(corners[i][0], corners[i][1]);
  }
  ctx.closePath();
  ctx.lineWidth = 2 / zoomLevel.value;
  ctx.strokeStyle = '#0072bd';
  ctx.stroke();

  const handle = OBB_HANDLE_SIZE / zoomLevel.value;
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = '#0072bd';
  ctx.lineWidth = 1.5 / zoomLevel.value;
  for (const [hx, hy] of corners) {
    ctx.fillRect(hx - handle / 2, hy - handle / 2, handle, handle);
    ctx.strokeRect(hx - handle / 2, hy - handle / 2, handle, handle);
  }

  const [kx, ky] = obbKnobWorld(b);
  // Top-edge midpoint to anchor the knob's tether.
  const topMidX = (corners[0][0] + corners[1][0]) / 2;
  const topMidY = (corners[0][1] + corners[1][1]) / 2;
  ctx.beginPath();
  ctx.moveTo(topMidX, topMidY);
  ctx.lineTo(kx, ky);
  ctx.strokeStyle = '#0072bd';
  ctx.lineWidth = 1.5 / zoomLevel.value;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(kx, ky, OBB_KNOB_RADIUS / zoomLevel.value, 0, Math.PI * 2);
  ctx.fillStyle = '#0072bd';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5 / zoomLevel.value;
  ctx.stroke();
}

function drawPolygonOverlay(ctx: CanvasRenderingContext2D) {
  const verts = polygonPoints.value;
  const preview = polygonPreviewPos.value;
  if (verts.length === 0 && !preview) {
    return;
  }
  const lw = 2 / zoomLevel.value;
  const accent = '#0072bd';
  const dash = 6 / zoomLevel.value;

  // Translucent fill of the would-be polygon (existing verts + cursor) so the
  // user can see what the resulting mask would look like before clicking.
  if (preview && verts.length >= 2) {
    ctx.beginPath();
    ctx.moveTo(verts[0][0], verts[0][1]);
    for (let i = 1; i < verts.length; i++) {
      ctx.lineTo(verts[i][0], verts[i][1]);
    }
    ctx.lineTo(preview[0], preview[1]);
    ctx.closePath();
    ctx.fillStyle = 'rgba(0, 114, 189, 0.18)';
    ctx.fill();
  }

  // Solid edges between consecutive vertices.
  if (verts.length >= 2) {
    ctx.beginPath();
    ctx.moveTo(verts[0][0], verts[0][1]);
    for (let i = 1; i < verts.length; i++) {
      ctx.lineTo(verts[i][0], verts[i][1]);
    }
    ctx.lineWidth = lw;
    ctx.strokeStyle = accent;
    ctx.setLineDash([]);
    ctx.stroke();
  }

  // Auto-closing edge from last back to first — dashed so it reads as implicit.
  if (verts.length >= 3) {
    ctx.beginPath();
    ctx.moveTo(verts[verts.length - 1][0], verts[verts.length - 1][1]);
    ctx.lineTo(verts[0][0], verts[0][1]);
    ctx.setLineDash([dash, dash]);
    ctx.lineWidth = lw;
    ctx.strokeStyle = accent;
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // Live-preview ghost edges from last vertex through cursor back to first.
  if (preview && verts.length >= 1) {
    ctx.setLineDash([dash, dash]);
    ctx.lineWidth = lw;
    ctx.strokeStyle = accent;
    ctx.beginPath();
    ctx.moveTo(verts[verts.length - 1][0], verts[verts.length - 1][1]);
    ctx.lineTo(preview[0], preview[1]);
    ctx.stroke();
    if (verts.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(preview[0], preview[1]);
      ctx.lineTo(verts[0][0], verts[0][1]);
      ctx.stroke();
    }
    ctx.setLineDash([]);
  }

  // Vertex handles.
  const baseR = 5 / zoomLevel.value;
  const handleLw = 1.5 / zoomLevel.value;
  for (let i = 0; i < verts.length; i++) {
    const [vx, vy] = verts[i];
    const hovered = i === hoveredPolygonVertex.value;
    const r = hovered ? baseR * 1.6 : baseR;
    if (hovered) {
      ctx.beginPath();
      ctx.arc(vx, vy, r + 4 / zoomLevel.value, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.18)';
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(vx, vy, r, 0, Math.PI * 2);
    ctx.fillStyle = hovered ? '#ef4444' : accent;
    ctx.fill();
    ctx.lineWidth = handleLw;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
    if (hovered) {
      const off = r * 0.5;
      ctx.beginPath();
      ctx.moveTo(vx - off, vy - off);
      ctx.lineTo(vx + off, vy + off);
      ctx.moveTo(vx + off, vy - off);
      ctx.lineTo(vx - off, vy + off);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2 / zoomLevel.value;
      ctx.stroke();
    }
  }

  // Ghost vertex at the cursor showing where the next click would land.
  if (preview && hoveredPolygonVertex.value === -1) {
    ctx.beginPath();
    ctx.arc(preview[0], preview[1], baseR, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 114, 189, 0.5)';
    ctx.fill();
    ctx.lineWidth = 1.5 / zoomLevel.value;
    ctx.strokeStyle = '#ffffff';
    ctx.stroke();
  }
}

const redraw = () => {
  if (!maskCanvas.value || !imageElement.value) {
    return;
  }
  const ctx = maskCanvas.value.getContext('2d');
  if (!ctx) {
    return;
  }
  const { width, height } = maskCanvas.value;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, width, height);

  ctx.setTransform(
    zoomLevel.value,
    0,
    0,
    zoomLevel.value,
    panOffset.value.x,
    panOffset.value.y,
  );

  if (viewMode.value === 'mask-only') {
    // Paint the black backdrop in image space so it shares the image's
    // aspect ratio and position (instead of filling the whole container).
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, imageElement.value.width, imageElement.value.height);
  } else {
    ctx.drawImage(imageElement.value, 0, 0);
  }
  if (viewMode.value !== 'image' && editableMaskData) {
    drawMaskOverlay(ctx);
  }
  if (inputMode.value === 'bbox' && obb.value) {
    drawOBBOverlay(ctx);
  }
  if (inputMode.value === 'segmentation') {
    drawPointMarkers(ctx);
  }
  if (inputMode.value === 'polygon') {
    drawPolygonOverlay(ctx);
  }
};

const drawMaskWithMaskData = (mask: MaskData, scores: number[]) => {
  if (!maskCanvas.value || !imageElement.value || !imageLoaded) {
    return;
  }
  const bestIndex =
    scores && scores.length > 1
      ? scores.reduce((best, score, i) => (score > scores[best] ? i : best), 0)
      : 0;

  const imageWidth = imageElement.value.width;
  const imageHeight = imageElement.value.height;
  const numPixels = imageWidth * imageHeight;
  const numMasks = scores ? scores.length : 1;
  editableMaskData = new Uint8ClampedArray(numPixels);
  for (let i = 0; i < numPixels; i++) {
    editableMaskData[i] = mask.data[i * numMasks + bestIndex] > 0 ? 255 : 0;
  }
  // SAM mask replaces any in-progress OBB or polygon.
  obb.value = null;
  polygonPoints.value = [];
  polygonPreviewPos.value = null;
  updateConnectivity();
  redraw();
  emitMaskUpdate();
  loadingSpinnerStore.setShowLoadingOverlay(false);
};

const resetMask = () => {
  if (!editableMaskData || !imageElement.value) {
    return;
  }
  points.value = [];
  obb.value = null;
  polygonPoints.value = [];
  hoveredPolygonVertex.value = -1;
  polygonPreviewPos.value = null;
  clearMaskBuffer();
  updateConnectivity();
  redraw();
  emitMaskUpdate();
};

const zoomIn = () => {
  zoomLevel.value = Math.min(zoomLevel.value * 1.2, 5.0);
  redraw();
};
const zoomOut = () => {
  zoomLevel.value = Math.max(zoomLevel.value / 1.2, 0.1);
  redraw();
};
const zoomToFit = () => {
  if (!maskCanvas.value || !props.imageSrc) {
    return;
  }
  const container = maskCanvas.value.parentElement;
  if (container === null) {
    return;
  }
  const img = new Image();
  img.onload = () => {
    const scaleX = (container.clientWidth - 20) / img.width;
    const scaleY = (container.clientHeight - 20) / img.height;
    zoomLevel.value = Math.min(scaleX, scaleY, 1.0);
    const scaledImageWidth = img.width * zoomLevel.value;
    panOffset.value = {
      x: (container.clientWidth - scaledImageWidth) / 2,
      y: 0,
    };
    redraw();
  };
  img.src = props.imageSrc;
};

const initializeCanvas = () => {
  if (!maskCanvas.value || !props.imageSrc) {
    return;
  }
  const img = new Image();
  img.onload = () => {
    if (!maskCanvas.value) {
      return;
    }
    const container = maskCanvas.value.parentElement;
    if (!container) {
      return;
    }
    maskCanvas.value.width = container.clientWidth;
    maskCanvas.value.height = container.clientHeight;

    imageLoaded = true;
    imageElement.value = img;
    const numPixels = img.width * img.height;
    editableMaskData = new Uint8ClampedArray(numPixels);

    // Rehydrate from the parent if we have a previously-labeled mask for
    // this image. We only accept a mask whose dims match the loaded image —
    // a mismatch means the parent's snapshot belongs to a different
    // resolution (e.g. after retryAtFullHd swapped the source file) and
    // we should fall back to a blank canvas rather than corrupt it.
    const seed = props.initialMask;
    if (
      seed &&
      seed.width === img.width &&
      seed.height === img.height &&
      seed.data.length === numPixels
    ) {
      editableMaskData.set(seed.data);
    }

    points.value = [];
    obb.value = null;
    polygonPoints.value = [];
    hoveredPolygonVertex.value = -1;
    polygonPreviewPos.value = null;
    updateConnectivity();
    zoomToFit();
    redraw();
    // Echo the rehydrated has-pixels flag so the parent's gating computed
    // doesn't have to scan a multi-megabyte byte array on every tick.
    emit('has-pixels-changed', hasMaskPixels.value);
  };
  img.src = props.imageSrc;
};

const handleWheel = (e: WheelEvent) => {
  if (!maskCanvas.value) {
    return;
  }
  e.preventDefault();
  const rect = maskCanvas.value.getBoundingClientRect();
  const mouseX = e.clientX - rect.left;
  const mouseY = e.clientY - rect.top;
  const oldZoom = zoomLevel.value;
  zoomLevel.value =
    e.deltaY > 0
      ? Math.max(zoomLevel.value / 1.1, 0.1)
      : Math.min(zoomLevel.value * 1.1, 5.0);
  const zoomRatio = zoomLevel.value / oldZoom;
  panOffset.value.x = mouseX - (mouseX - panOffset.value.x) * zoomRatio;
  panOffset.value.y = mouseY - (mouseY - panOffset.value.y) * zoomRatio;
  redraw();
};

function applyCursor() {
  if (!maskCanvas.value) {
    return;
  }
  if (cursorOverride) {
    maskCanvas.value.style.cursor = cursorOverride;
  } else if (inputMode.value === 'pan') {
    maskCanvas.value.style.cursor =
      activeDrag?.kind === 'pan' ? 'grabbing' : 'grab';
  } else if (inputMode.value === 'drawing') {
    maskCanvas.value.style.cursor = isErasing.value ? 'cell' : 'crosshair';
  } else if (inputMode.value === 'bbox') {
    maskCanvas.value.style.cursor = 'crosshair';
  } else if (inputMode.value === 'polygon') {
    maskCanvas.value.style.cursor = 'crosshair';
  } else {
    maskCanvas.value.style.cursor = 'crosshair';
  }
}

watch(
  () => props.imageSrc,
  () => {
    if (props.imageSrc) {
      zoomLevel.value = 1.0;
      panOffset.value = { x: 0, y: 0 };
      initializeCanvas();
    }
  },
);

let isRevertingMode = false;
// Set when the user accepted the "Clear current mask?" dialog and we are
// re-applying the originally-requested mode change. Without this flag the
// polygon → bbox transition (whose rule is unconditional) re-triggers the
// same confirm popup, which pops the dialog open again and reverts the
// mode back to the old value via the recursive watcher fire.
let isApplyingConfirmedSwitch = false;
// Pan is content-free, so transitions through it must use the last "real"
// mode for rule decisions — otherwise polygon → pan → sam would silently
// skip the confirm popup.
let lastNonPanMode: InputMode = 'segmentation';

watch(inputMode, (newMode, oldMode) => {
  if (isRevertingMode) {
    isRevertingMode = false;
    return;
  }

  // Going INTO pan: always free. Remember where we came from.
  if (newMode === 'pan') {
    if (oldMode !== 'pan') {
      lastNonPanMode = oldMode;
    }
    applyCursor();
    redraw();
    return;
  }

  // Going OUT of pan: apply rules as if we were in the last real mode.
  const effectiveOld: InputMode = oldMode === 'pan' ? lastNonPanMode : oldMode;

  // Round-trip through pan back to the same mode is a no-op.
  if (effectiveOld === newMode) {
    applyCursor();
    redraw();
    return;
  }

  const isRasterMode = (m: InputMode) =>
    m === 'segmentation' || m === 'drawing';

  const modeLabels: Record<InputMode, string> = {
    segmentation: $gettext('AI Segmentation'),
    drawing: $gettext('Freeform Brush'),
    bbox: $gettext('BBox'),
    polygon: $gettext('Polygon'),
    pan: $gettext('Pan & Zoom'),
  };

  // BBox → Polygon: convert OBB's 4 corners into polygon vertices (free, no
  // confirm — the conversion is lossless).
  if (effectiveOld === 'bbox' && newMode === 'polygon' && obb.value) {
    polygonPoints.value = obbCornersWorld(obb.value);
    obb.value = null;
    rasterizeCurrentPolygon(true);
    emitMaskUpdate();
    applyCursor();
    redraw();
    return;
  }

  // Transitions that can't preserve current annotation state. We confirm
  // (Continue clears the mask + applies the switch) instead of blocking:
  //   - Polygon → BBox: no clean conversion direction.
  //   - Raster (SAM/Drawing) → BBox/Polygon with content: pixel mask can't be
  //     captured as a parametric shape.
  //   - Anything → SAM with content: SAM clicks always replace the mask.
  const needsClearConfirm =
    (effectiveOld === 'polygon' && newMode === 'bbox') ||
    (isRasterMode(effectiveOld) &&
      (newMode === 'bbox' || newMode === 'polygon') &&
      hasAnyContent.value) ||
    (newMode === 'segmentation' &&
      effectiveOld !== 'segmentation' &&
      hasAnyContent.value);

  if (needsClearConfirm && !isApplyingConfirmedSwitch) {
    isRevertingMode = true;
    inputMode.value = oldMode;
    modeDialog.value = {
      title: $gettext('Clear current mask?'),
      message: $gettext(
        'Switching to %{mode} will clear the current mask. Continue?',
        { mode: modeLabels[newMode] },
      ),
      onConfirm: () => {
        resetMask();
        // Apply the originally-requested switch. The bypass flag stops the
        // watcher from re-prompting for the same transition (e.g. the
        // polygon → bbox rule fires unconditionally).
        isApplyingConfirmedSwitch = true;
        inputMode.value = newMode;
      },
    };
    return;
  }
  isApplyingConfirmedSwitch = false;

  // BBox → Drawing (free): commit OBB to raster mask, keep mask visible.
  if (effectiveOld === 'bbox' && newMode === 'drawing' && obb.value) {
    commitOBBToMask();
    emitMaskUpdate();
  }

  // Polygon → anything-else (free): mask reflects the polygon, drop verts.
  if (effectiveOld === 'polygon' && newMode !== 'polygon') {
    polygonPoints.value = [];
    hoveredPolygonVertex.value = -1;
    polygonPreviewPos.value = null;
  }

  applyCursor();
  redraw();
});

watch(isErasing, () => applyCursor());
watch(promptMode, () => applyCursor());
watch(viewMode, () => redraw());

watch(
  () => props.currentMask,
  (newMask) => {
    if (newMask && newMask.mask && newMask.scores) {
      drawMaskWithMaskData(newMask.mask, newMask.scores);
    }
  },
  { immediate: true, deep: true },
);

function emitLabelInfo() {
  emit('set-class', { className: className.value });
  if (
    sizeWidth.value !== null &&
    sizeWidth.value >= props.minDimension &&
    sizeHeight.value !== null &&
    sizeHeight.value >= props.minDimension &&
    sizeDepth.value !== null &&
    sizeDepth.value >= props.minDimension
  ) {
    emit('set-size', {
      width: sizeWidth.value,
      height: sizeHeight.value,
      depth: sizeDepth.value,
    });
  }
}

// While the size is locked (another image owns this class's dimensions),
// mirror the parent's value into the local refs — stepper items stay mounted
// after their first visit, so the onMounted rehydration below never re-runs
// and owner edits would otherwise not reach this instance. The snapshot
// protects dims the user already typed when the lock engages only
// transiently, e.g. typing "boxes" passes through the existing class "box".
let preLockSizes: {
  width: number | null;
  height: number | null;
  depth: number | null;
} | null = null;
let lockedForClass: string | null = null;

watch(
  () => [props.sizeLocked, props.initialSize] as const,
  ([locked], previous) => {
    const wasLocked = previous?.[0] ?? false;
    if (locked && !wasLocked) {
      preLockSizes = {
        width: sizeWidth.value,
        height: sizeHeight.value,
        depth: sizeDepth.value,
      };
      lockedForClass = className.value;
    }
    if (locked) {
      sizeWidth.value = props.initialSize?.width ?? null;
      sizeHeight.value = props.initialSize?.height ?? null;
      sizeDepth.value = props.initialSize?.depth ?? null;
    } else if (wasLocked) {
      if (className.value !== lockedForClass && preLockSizes) {
        // Unlocked because the class changed, not by ownership transfer:
        // restore what the user had typed before the transient lock.
        sizeWidth.value = preLockSizes.width;
        sizeHeight.value = preLockSizes.height;
        sizeDepth.value = preLockSizes.depth;
        emitLabelInfo();
      }
      // Ownership transfer (class unchanged): keep the current dims,
      // the fields simply unlock.
      preLockSizes = null;
      lockedForClass = null;
    }
  },
  { deep: true },
);

onMounted(() => {
  // Rehydrate label inputs from props before the canvas is ready so the
  // user sees their previously-typed class/size when navigating back. The
  // parent already holds these values in imageClasses/imageSizes — the
  // local refs reset on every remount, so we have to copy them in.
  if (props.initialClassName) {
    className.value = props.initialClassName;
  }
  if (props.initialSize) {
    sizeWidth.value = props.initialSize.width;
    sizeHeight.value = props.initialSize.height;
    sizeDepth.value = props.initialSize.depth;
  }
  if (props.imageSrc) {
    initializeCanvas();
  }
});
</script>

<template>
  <div class="labeling-container">
    <div class="toolbar">
      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props: a }">
          <v-btn
            v-bind="a"
            variant="outlined"
            rounded="pill"
            icon="mdi-magnify-minus"
            size="small"
            @click="zoomOut"
          />
        </template>
        {{ $gettext('Zoom out') }}
      </v-tooltip>

      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props: a }">
          <v-btn
            v-bind="a"
            variant="outlined"
            rounded="pill"
            icon="mdi-magnify-plus"
            size="small"
            @click="zoomIn"
          />
        </template>
        {{ $gettext('Zoom in') }}
      </v-tooltip>

      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props: a }">
          <v-btn
            v-bind="a"
            variant="outlined"
            rounded="pill"
            icon="mdi-fit-to-screen"
            size="small"
            @click="zoomToFit"
          />
        </template>
        {{ $gettext('Fit to Window') }}
      </v-tooltip>

      <v-tooltip anchor="bottom" location="top">
        <template v-slot:activator="{ props: a }">
          <v-btn
            v-bind="a"
            variant="outlined"
            rounded="pill"
            icon="mdi-restart"
            size="small"
            @click="resetMask"
          />
        </template>
        {{ $gettext('Reset Label') }}
      </v-tooltip>

      <span style="width: 45px">{{ Math.round(zoomLevel * 100) }}%</span>

      <v-btn-toggle
        v-model="viewMode"
        color="primary"
        :mandatory="true"
        style="height: 38px"
      >
        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn v-bind="a" icon="mdi-image" size="small" value="image" />
          </template>
          {{ $gettext('Image only') }}
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn v-bind="a" icon="mdi-layers" size="small" value="overlay" />
          </template>
          {{ $gettext('Image + mask overlay') }}
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              icon="mdi-checkerboard"
              size="small"
              value="mask-only"
            />
          </template>
          {{ $gettext('Mask only') }}
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle
        v-model="inputMode"
        color="primary"
        :mandatory="true"
        style="height: 38px"
      >
        <v-tooltip anchor="bottom" location="top" max-width="280">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              size="small"
              icon="mdi-vector-polygon"
              value="segmentation"
            />
          </template>
          <div>
            <strong>{{ $gettext('AI-based segmentation') }}</strong>
            <div>
              {{
                $gettext(
                  'Click on the object and the AI will suggest a mask. Add positive points to extend it, negative points to exclude regions. Click an existing dot to remove it.',
                )
              }}
            </div>
          </div>
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top" max-width="280">
          <template v-slot:activator="{ props: a }">
            <v-btn v-bind="a" icon="mdi-draw" size="small" value="drawing" />
          </template>
          <div>
            <strong>{{ $gettext('Freeform brush') }}</strong>
            <div>
              {{
                $gettext(
                  'Drag to paint a mask. Toggle the eraser to clean up. Adjust the brush size with the slider.',
                )
              }}
            </div>
          </div>
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top" max-width="280">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              icon="mdi-vector-rectangle"
              size="small"
              value="bbox"
            />
          </template>
          <div>
            <strong>{{ $gettext('Rotated bounding box') }}</strong>
            <div>
              {{
                $gettext(
                  'Drag a rectangle around the object. Use the knob to rotate, corners to resize, body to move.',
                )
              }}
            </div>
          </div>
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top" max-width="280">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              icon="mdi-vector-polyline"
              size="small"
              value="polygon"
            />
          </template>
          <div>
            <strong>{{ $gettext('Polygon') }}</strong>
            <div>
              {{
                $gettext(
                  'Click to add vertices. The polygon auto-closes between the last and first point. Click an existing vertex to remove it.',
                )
              }}
            </div>
          </div>
        </v-tooltip>
      </v-btn-toggle>

      <v-btn-toggle
        v-model="inputMode"
        color="primary"
        :mandatory="true"
        style="height: 38px"
      >
        <v-tooltip anchor="bottom" location="top" max-width="280">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              icon="mdi-hand-back-right-outline"
              size="small"
              value="pan"
            />
          </template>
          <div>
            <strong>{{ $gettext('Pan & zoom') }}</strong>
            <div>
              {{
                $gettext(
                  'Move the image with click + drag, zoom with the wheel. No annotations are created in this mode.',
                )
              }}
            </div>
          </div>
        </v-tooltip>
      </v-btn-toggle>

      <template v-if="inputMode === 'segmentation'">
        <v-btn-toggle
          v-model="promptMode"
          :mandatory="true"
          divided
          variant="outlined"
          style="height: 38px"
        >
          <v-tooltip anchor="bottom" location="top" max-width="280">
            <template v-slot:activator="{ props: a }">
              <v-btn
                v-bind="a"
                size="small"
                value="positive"
                :color="promptMode === 'positive' ? 'success' : undefined"
              >
                <v-icon start>mdi-plus-thick</v-icon>
                {{ $gettext('Add') }}
              </v-btn>
            </template>
            <div>
              <strong>{{ $gettext('Add (positive prompt)') }}</strong>
              <div>
                {{
                  $gettext(
                    'Click on a part of the object you want to include in the mask. The AI extends the mask to cover that region.',
                  )
                }}
              </div>
            </div>
          </v-tooltip>
          <v-tooltip anchor="bottom" location="top" max-width="280">
            <template v-slot:activator="{ props: a }">
              <v-btn
                v-bind="a"
                size="small"
                value="negative"
                :color="promptMode === 'negative' ? 'error' : undefined"
              >
                <v-icon start>mdi-minus-thick</v-icon>
                {{ $gettext('Exclude') }}
              </v-btn>
            </template>
            <div>
              <strong>{{ $gettext('Exclude (negative prompt)') }}</strong>
              <div>
                {{
                  $gettext(
                    'Click on a region the mask should NOT include — useful when the AI grabs something extra. Right-click anywhere does the same.',
                  )
                }}
              </div>
            </div>
          </v-tooltip>
        </v-btn-toggle>

        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              variant="outlined"
              rounded="pill"
              icon="mdi-undo"
              size="small"
              :disabled="points.length === 0"
              @click="handleUndoLastPoint"
            />
          </template>
          {{ $gettext('Undo last point') }}
        </v-tooltip>

        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn
              v-bind="a"
              variant="outlined"
              rounded="pill"
              icon="mdi-close"
              size="small"
              :disabled="points.length === 0"
              @click="handleClearPoints"
            />
          </template>
          {{ $gettext('Clear all points') }}
        </v-tooltip>
      </template>

      <v-btn-toggle
        v-if="inputMode === 'drawing'"
        v-model="isErasing"
        color="primary"
        :mandatory="true"
        class="mx-4"
        style="height: 38px"
      >
        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn v-bind="a" icon="mdi-brush" size="small" :value="false" />
          </template>
          {{ $gettext('Brush') }}
        </v-tooltip>
        <v-tooltip anchor="bottom" location="top">
          <template v-slot:activator="{ props: a }">
            <v-btn v-bind="a" icon="mdi-eraser" size="small" :value="true" />
          </template>
          {{ $gettext('Eraser') }}
        </v-tooltip>
      </v-btn-toggle>

      <v-row v-if="inputMode === 'drawing'" class="align-center" no-gutters>
        <v-col>
          <v-tooltip anchor="bottom" location="top">
            <template v-slot:activator="{ props: a }">
              <v-slider
                v-bind="a"
                v-model="brushSize"
                :min="1"
                :max="20"
                step="1"
                hide-details
                dense
                style="max-width: 200px"
              >
                <template v-slot:prepend>
                  <v-icon color="primary">mdi-brush</v-icon>
                </template>
              </v-slider>
            </template>
            {{ $gettext('Brush Size') }}
          </v-tooltip>
        </v-col>
      </v-row>

      <span v-if="inputMode === 'bbox' && obb" class="bbox-rotation-readout">
        {{ $gettext('Rotation:') }}
        {{ Math.round(((obb.angleRad * 180) / Math.PI + 360) % 360) }}°
      </span>

      <span
        v-if="inputMode === 'segmentation' && !embeddingsReady"
        class="embeddings-status"
      >
        <v-progress-circular indeterminate size="14" width="2" />
        {{ $gettext('Loading model — clicks disabled') }}
      </span>
    </div>

    <div v-if="ccCount > 1" class="connectivity-warning">
      <v-icon color="warning" size="small">mdi-alert</v-icon>
      <span class="ml-2">
        {{
          $gettext(
            'Mask has %{count} disconnected parts. A single contiguous region is expected.',
            {
              count: String(ccCount),
            },
          )
        }}
      </span>
      <v-spacer />
      <v-btn
        size="x-small"
        variant="tonal"
        color="primary"
        @click="handleKeepLargest"
      >
        {{ $gettext('Keep largest') }}
      </v-btn>
      <v-btn
        size="x-small"
        variant="tonal"
        color="primary"
        class="ml-2"
        @click="handleFillHoles"
      >
        {{ $gettext('Fill holes') }}
      </v-btn>
      <v-btn
        size="x-small"
        variant="text"
        class="ml-2"
        :disabled="ignoreDisconnected"
        @click="handleIgnoreDisconnected"
      >
        {{ ignoreDisconnected ? $gettext('Ignored') : $gettext('Ignore') }}
      </v-btn>
    </div>

    <div class="sidebar">
      <div class="sidebar-content">
        <div v-if="showSingleInstanceHint" class="single-instance-hint">
          <v-icon size="small" color="primary">
            mdi-information-outline
          </v-icon>
          <span>
            {{
              $gettext(
                'Mark one object per image. Add more images for more objects.',
              )
            }}
          </span>
          <v-btn
            icon="mdi-close"
            size="x-small"
            variant="text"
            density="compact"
            class="single-instance-hint__close"
            :aria-label="$gettext('Dismiss hint')"
            @click="dismissSingleInstanceHint"
          />
        </div>
        <h4>{{ $gettext('Label Properties') }}</h4>
        <v-form
          ref="form"
          v-model="inputIsValid"
          @update:model-value="emitValidity"
        >
          <v-combobox
            v-model="className"
            :items="existingClassNames"
            :label="$gettext('Class Name')"
            :placeholder="$gettext('e.g. cardboard_box')"
            persistent-placeholder
            dense
            class="mb-3"
            clearable
            :rules="[requiredRule, noSpacesRule]"
            @update:model-value="emitLabelInfo"
          />
          <v-tooltip
            :text="
              $gettext(
                'Enter Object Length along the axis of the coordinate system in mm (min: %{min_size}mm, max: %{max_size}mm)',
                {
                  min_size: String(props.minDimension),
                  max_size: String(props.maxDimension),
                },
              )
            "
            location="bottom"
          >
            <template #activator="{ props: a }">
              <v-text-field
                v-model.number="sizeWidth"
                class="mt-4"
                :label="$gettext('X')"
                type="number"
                density="compact"
                hide-details
                suffix="mm"
                :disabled="props.sizeLocked"
                :rules="[requiredRule, objectSizeRule]"
                :min="props.minDimension"
                :max="props.maxDimension"
                v-bind="a"
                @input="emitLabelInfo"
              />
            </template>
          </v-tooltip>
          <v-tooltip
            :text="
              $gettext(
                'Enter Object Length along the axis of the coordinate system in mm (min: %{min_size}mm, max: %{max_size}mm)',
                {
                  min_size: String(props.minDimension),
                  max_size: String(props.maxDimension),
                },
              )
            "
            location="bottom"
          >
            <template #activator="{ props: a }">
              <v-text-field
                v-model.number="sizeHeight"
                class="mt-4"
                :label="$gettext('Y')"
                type="number"
                density="compact"
                hide-details="auto"
                validate-on="input"
                suffix="mm"
                :disabled="props.sizeLocked"
                :rules="[requiredRule, objectSizeRule]"
                :min="props.minDimension"
                :max="props.maxDimension"
                v-bind="a"
                @input="emitLabelInfo"
              />
            </template>
          </v-tooltip>
          <v-tooltip
            :text="
              $gettext(
                'Enter Object Length along the axis of the coordinate system in mm (min: %{min_size}mm, max: %{max_size}mm)',
                {
                  min_size: String(props.minDimension),
                  max_size: String(props.maxDimension),
                },
              )
            "
            location="bottom"
          >
            <template #activator="{ props: a }">
              <v-text-field
                v-model.number="sizeDepth"
                class="mt-4"
                :label="$gettext('Z')"
                type="number"
                density="compact"
                hide-details
                suffix="mm"
                :disabled="props.sizeLocked"
                :rules="[requiredRule, objectSizeRule]"
                :min="props.minDimension"
                :max="props.maxDimension"
                v-bind="a"
                @input="emitLabelInfo"
              />
            </template>
          </v-tooltip>
          <div
            v-if="props.sizeLocked"
            class="text-caption text-medium-emphasis mt-2"
          >
            {{
              $gettext(
                'Dimensions are defined by the class and can only be edited on the first image that uses it.',
              )
            }}
          </div>
        </v-form>
        <div class="mb-2">
          <img
            src="@/assets/coordinate_system.png"
            alt="Coordinate System"
            style="
              width: 100%;
              max-width: 120px;
              display: block;
              margin: 8px auto;
              border-radius: 16px;
            "
          />
        </div>
      </div>
    </div>

    <div class="canvas-wrapper">
      <canvas
        ref="maskCanvas"
        @wheel="handleWheel"
        @mousedown="handleMouseDown"
        @mousemove="handleMouseMove"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseLeave"
        @contextmenu="handleContextMenu"
      ></canvas>
    </div>

    <v-dialog
      :model-value="modeDialog !== null"
      max-width="460"
      persistent
      @update:model-value="(v) => !v && (modeDialog = null)"
    >
      <v-card v-if="modeDialog">
        <v-card-title class="text-h6">{{ modeDialog.title }}</v-card-title>
        <v-card-text>{{ modeDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="modeDialog = null">
            {{ $gettext('Back') }}
          </v-btn>
          <v-btn
            color="primary"
            variant="tonal"
            @click="
              () => {
                const cb = modeDialog?.onConfirm;
                modeDialog = null;
                cb?.();
              }
            "
          >
            {{ $gettext('Continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped lang="scss">
.labeling-container {
  height: 100%;
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  border-radius: 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  background-color: rgba(245, 245, 245, 1);
  border-bottom: 1px solid #ddd;
  align-items: center;
  margin-right: 220px;
}

.bbox-rotation-readout {
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: #444;
  margin-left: 8px;
}

.embeddings-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #b26a00;
  background: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 6px;
  padding: 2px 10px;
}

.connectivity-warning {
  display: flex;
  align-items: center;
  margin: 8px 232px 0 12px;
  padding: 8px 12px;
  background-color: #fff8e1;
  border: 1px solid #ffd54f;
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.sidebar {
  position: absolute;
  top: 0;
  right: 0;
  width: 220px;
  height: 100%;
  padding: 12px;
  background-color: rgba(245, 245, 245, 1);
  border-left: 1px solid #ddd;
  z-index: 10;
  box-shadow: -2px 0 5px rgba(0, 0, 0, 0.05);
  overflow-y: auto;
  overscroll-behavior: contain;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.single-instance-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: #eef4fb;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.35;
  color: #334155;
}

.single-instance-hint__close {
  margin-left: auto;
  margin-top: -2px;
  flex-shrink: 0;
}

@media (max-height: 760px) {
  .sidebar {
    padding: 8px;
  }

  .sidebar-content {
    gap: 8px;
  }

  .single-instance-hint {
    padding: 6px 8px;
    font-size: 11px;
  }

  .sidebar-content img[alt='Coordinate System'] {
    max-width: 90px !important;
    margin: 4px auto !important;
  }
}

.canvas-wrapper {
  flex: 1;
  overflow: hidden;
  background-color: #f9f9f9;
  background-image: radial-gradient(circle, #ccc 1px, transparent 1px);
  background-size: 20px 20px;
  position: relative;
  cursor: crosshair;
  margin-right: 220px;
}

.canvas-wrapper:active {
  cursor: grabbing;
}

canvas {
  display: block;
  cursor: inherit;
  image-rendering: pixelated;
}

.status {
  padding: 10px;
  background-color: #f5f5f5;
  border-top: 1px solid #ddd;
  font-size: 14px;
  flex-shrink: 0;
}

input[type='range'] {
  width: 100px;
}
</style>
